/**
 * Inner Sanctuary — Session History + Saved Insight Cards
 *
 * The user's record of what they've brought and what they've chosen.
 * Each complete session is an expandable card: emotion, date, pattern,
 * choice, summary, and any saved Insight Cards from that session.
 *
 * The Inner Sanctuary is the reason to return.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/supabase'
import { callClaude } from '../lib/claude'
import { saveInsightCard } from '../lib/shareCard'
import { redirectToCheckout } from '../lib/stripe'
import { trackUpgradePromptShown } from '../lib/analytics'
import { fetchBecoming } from '../lib/becomingGenerator'
import type { BecomingRecord } from '../lib/becomingGenerator'
import type { SessionData } from '../types/session'
import { EMOTIONS } from '../data/emotions'
import { SYMBOLS } from '../data/symbols'
import { SERIES_DATA } from '../data/series'
import { localizeSeries } from '../data/series.pl'
import { getArc, type ArcId } from '../data/arcs'
import { localizeArc } from '../data/arcs.pl'
import { localizeEmotion, localizeEmotionLabel } from '../data/emotions.pl'
import { localizeArchetype } from '../lib/archetypes'
import { pluralCategory } from '../lib/plural'
import { useTranslation } from 'react-i18next'

interface PatternRecord {
  id: string
  pattern_label: string
  occurrence_count: number
  first_seen_at: string
  last_seen_at: string
  session_ids: string[]
}

// ── Archetype types (Phase 2 — Awakening tier only) ──────────────────────────
interface ArchetypeDefinition {
  id: string
  display_name: string
  description: string
  shadow_aspect: string
  integration_aspect: string
}

interface ArchetypeAssignment {
  id: string
  archetype_id: string
  state: string
  intensity: number
  observation_count: number
  user_confirmed: boolean
  user_rejected: boolean
  evidence_quote: string | null
  first_seen_at: string
}

// Combined assignment with definition data — what we render in the UI.
interface ArchetypeWithDef extends ArchetypeAssignment {
  definition: ArchetypeDefinition
}

interface SeriesProgressRecord {
  series_id: string
  current_position: number
  completed_at: string | null
}

// ── Cluster types (Phase 3 — Awakening tier only) ────────────────────────────
// pattern_clusters represents a recurring theme stitched together from many
// pattern_signals across the user's sessions. Surfaced in the Inner Sanctuary
// once a cluster has 2+ member signals and isn't currently dismissed.
interface ClusterRecord {
  id: string
  cluster_label: string
  occurrence_count: number
  first_seen_at: string
  dismissed_until: string | null
}


interface JournalProps {
  user: User
}

interface InsightCardRecord {
  id: string
  session_id: string
  card_type: string
  card_title: string
  card_message: string
  saved: boolean
  created_at: string
}

interface ArcLetterRecord {
  id: string
  arc_id: string
  session_number: number
  letter_content: string
  saved_at: string
}

// Arc accent colors — match src/data/arcs.ts.
// Deliberately colors only: the arc name and its session titles are NOT
// duplicated here. They come from the arc data through localizeArc(), so a
// Polish reader gets translated copy instead of a hardcoded English table.
const ARC_COLORS: Record<string, { color: string; colorSoft: string }> = {
  letters_to_others: { color: '#9B72C0', colorSoft: 'rgba(155,114,192,0.06)' },
  letters_to_self:   { color: '#C45272', colorSoft: 'rgba(196,82,114,0.06)' },
}

function LetterCard({ letter }: { letter: ArcLetterRecord }) {
  const { t, i18n } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const colors = ARC_COLORS[letter.arc_id] ?? ARC_COLORS.letters_to_others
  // Arc copy is looked up from the arc data and localized. An unrecognised
  // arc_id leaves `arc` undefined, and a session_number past the arc's
  // sessions falls back to the generic "Session N" label.
  const arcDef = getArc(letter.arc_id as ArcId)
  const arc = arcDef ? localizeArc(arcDef, i18n.language) : undefined
  const sessionTitle = arc?.sessions[letter.session_number - 1]?.title
    ?? t('common.sessionN', { n: letter.session_number })
  const date = new Date(letter.saved_at).toLocaleDateString(i18n.language, {
    month: 'long', day: 'numeric', year: 'numeric',
  })
  // Show a short preview (first 120 chars) in collapsed state
  const preview = letter.letter_content.length > 120
    ? letter.letter_content.slice(0, 120).trimEnd() + '...'
    : letter.letter_content

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'white',
        borderTop: `3px solid ${colors.color}`,
        borderRight: '1.5px solid #E4D5EE',
        borderBottom: '1.5px solid #E4D5EE',
        borderLeft: '1.5px solid #E4D5EE',
      }}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left px-5 py-4 flex items-start gap-3"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium uppercase tracking-widest" style={{ color: colors.color }}>
              {arc?.name}
            </p>
            <p className="text-xs" style={{ color: '#C4B5D4' }}>{date}</p>
          </div>
          <p className="text-sm font-medium mb-1" style={{ color: '#1C1128' }}>{sessionTitle}</p>
          {!expanded && (
            <p className="text-xs leading-relaxed" style={{ color: '#9B8FAA', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
              {preview}
            </p>
          )}
        </div>
        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none"
          className="flex-shrink-0 mt-0.5"
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: '#C4B5D4' }}
        >
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t" style={{ borderColor: '#F4EFF8' }}>
          <p
            className="text-sm leading-relaxed pt-4 whitespace-pre-wrap"
            style={{
              color: '#3D1F62',
              fontFamily: 'Georgia, serif',
              lineHeight: 1.85,
            }}
          >
            {letter.letter_content}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Quiet milestones — earned by phase reached, never shown during session.
 *
 * Structure only: the label and description are looked up per render at
 * `journal.milestones.<id>.label` / `.description`, so they translate with
 * the rest of the UI instead of living in an English table here.
 */
interface Milestone {
  id: string
  minPhase: number
  icon: React.ReactNode
  color: string
}

const MILESTONES: Milestone[] = [
  {
    id: 'traced',
    minPhase: 2,
    color: '#3A9B7A',
    icon: (
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <path d="M5.5 1v9M2 6.5l3.5 3.5L9 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'noticed',
    minPhase: 3,
    color: '#7B40A8',
    icon: (
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <circle cx="5.5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="5.5" cy="5.5" r="4.5" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      </svg>
    ),
  },
  {
    id: 'shifted',
    minPhase: 4,
    color: '#B87D0A',
    icon: (
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <path d="M2 5.5h7M6.5 3l2.5 2.5L6.5 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'chose',
    minPhase: 6,
    color: '#9B72C0',
    icon: (
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <path d="M2 6l2.5 2.5L9 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
]

const EMOTION_COLORS: Record<string, string> = {
  grief: '#7B6B9B', sadness: '#6B8FAB', anger: '#C06B6B', shame: '#9B7B9B',
  fear: '#7B9B8F', guilt: '#9B8F6B', loneliness: '#6B7B9B', hopelessness: '#7B7B8F',
  betrayal: '#9B6B7B', powerlessness: '#8F7B9B', disappointment: '#9B8F7B',
  resentment: '#8F6B6B', overwhelm: '#9B7B6B', anxiety: '#8F9B6B',
  heartache: '#C06B87', despair: '#6B6B8F', helplessness: '#7B8F8F',
  bitterness: '#8F7B6B', jealousy: '#9B9B6B', envy: '#7B9B7B',
  rejection: '#9B6B8F', confusion: '#7B8FAB', emptiness: '#8F8F9B',
  numbness: '#9B9B9B', restlessness: '#9B8B6B', dread: '#6B7B8F',
  frustration: '#C08B6B', regret: '#8B7B9B', inadequacy: '#9B7B8B',
  isolation: '#6B8B9B', humiliation: '#B06B7B', worthlessness: '#8B7B7B',
  'self-criticism': '#9B7B7B', disgust: '#7B9B7B', control: '#7B8B9B',
}

// Card-type display names are translated — see `journal.cardTypes.<card_type>`.
// Only the gradients stay here, because they are styling rather than copy.
const CARD_TYPE_GRADIENTS: Record<string, string> = {
  pattern_archetype: 'linear-gradient(135deg, #2A1245 0%, #4A2080 100%)',
  soul_perspective: 'linear-gradient(135deg, #0F2040 0%, #1A3A70 100%)',
  reframe_invitation: 'linear-gradient(135deg, #0F2A1A 0%, #1A4A2A 100%)',
  message_within: 'linear-gradient(135deg, #2A1020 0%, #501030 100%)',
}

function emotionColor(emotion: string): string {
  return EMOTION_COLORS[emotion.toLowerCase()] ?? '#9B72C0'
}

function SessionCard({ session, insightCards }: { session: SessionData; insightCards: InsightCardRecord[] }) {
  const { t, i18n } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  // emotionColor keys off the stored English word, so it takes the raw label.
  const color = emotionColor(session.emotion_label)
  const date = new Date(session.created_at).toLocaleDateString(i18n.language, {
    month: 'long', day: 'numeric', year: 'numeric',
  })
  // Healing phrase in the user's language; the lookup key stays the stored English word.
  const emotionData = EMOTIONS.find(e => e.word === session.emotion_label)
  const healingPhrase = emotionData ? localizeEmotion(emotionData, i18n.language).healingPhrase : null
  const earnedMilestones = MILESTONES.filter(m => session.phase_reached >= m.minPhase)
  const savedCards = insightCards.filter(c => c.session_id === session.id)

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'white',
        // Top edge carries the emotion's color — gives each card a subtle identity
        borderTop: `3px solid ${color}`,
        borderRight: '1.5px solid #E4D5EE',
        borderBottom: '1.5px solid #E4D5EE',
        borderLeft: '1.5px solid #E4D5EE',
        boxShadow: expanded ? '0 4px 24px rgba(61,31,98,0.07)' : 'none',
        transition: 'box-shadow 0.2s',
      }}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left px-5 py-4 flex items-center gap-3"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium capitalize flex items-center gap-1.5" style={{ color: '#1C1128' }}>
            {localizeEmotionLabel(session.emotion_label, i18n.language)}
            {session.emotion_intensity && (
              <span className="text-xs font-normal" style={{ color: '#9B8FAA' }}>
                ·{session.emotion_intensity}/10
              </span>
            )}
            {session.is_favorite && (
              <svg width="11" height="11" viewBox="0 0 14 14" fill="#B87D0A" aria-label={t('journal.keyInsight')}>
                <polygon points="7,1 9,5 13,5.5 10,8.5 10.5,13 7,11 3.5,13 4,8.5 1,5.5 5,5" />
              </svg>
            )}
          </p>
          <p className="text-xs mt-0.5" style={{ color: '#9B8FAA' }}>{date}</p>
        </div>

        {earnedMilestones.length > 0 && (
          <div className="flex gap-1.5 flex-shrink-0">
            {earnedMilestones.map(m => (
              <span
                key={m.id}
                title={t(`journal.milestones.${m.id}.label`)}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px]"
                style={{ background: '#F4EFF8', color: '#9B72C0', border: '1px solid #E4D5EE' }}
              >
                {m.icon}
                {t(`journal.milestones.${m.id}.label`)}
              </span>
            ))}
          </div>
        )}

        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none"
          style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: '#C4B5D4' }}
        >
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t" style={{ borderColor: '#F4EFF8' }}>

          {!session.is_complete && (
            <div className="pt-4">
              <p className="text-xs" style={{ color: '#9B8FAA' }}>
                {t('journal.reachedPhase', { n: session.phase_reached })}
              </p>
            </div>
          )}

          {session.is_complete && (
            <div className="pt-4 space-y-5">

              {session.pattern_reflection && (
                <div
                  className="px-4 py-3 rounded-xl"
                  style={{
                    background: 'rgba(155,114,192,0.04)',
                    borderLeft: '3px solid #9B72C0',
                    borderTop: '1px solid #E4D5EE',
                    borderRight: '1px solid #E4D5EE',
                    borderBottom: '1px solid #E4D5EE',
                  }}
                >
                  <p className="text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: '#9B72C0' }}>
                    {t('journal.patternSeen')}
                  </p>
                  <p className="text-sm leading-relaxed font-display italic" style={{ color: '#3D1F62' }}>
                    {session.pattern_reflection}
                  </p>
                </div>
              )}

              {session.choice_text && (
                <div
                  className="px-4 py-3 rounded-xl"
                  style={{ background: '#3D1F62' }}
                >
                  <p className="text-xs font-medium uppercase tracking-widest mb-1"
                    style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {t('journal.iChose')}
                  </p>
                  <p className="text-sm font-display italic leading-relaxed"
                    style={{ color: 'white', fontWeight: 400 }}>
                    {session.choice_text}
                  </p>
                </div>
              )}

              {session.session_summary && (
                <div
                  className="px-4 py-3 rounded-xl"
                  style={{
                    background: 'rgba(155,114,192,0.04)',
                    borderLeft: '3px solid #9B72C0',
                    borderTop: '1px solid #E4D5EE',
                    borderRight: '1px solid #E4D5EE',
                    borderBottom: '1px solid #E4D5EE',
                  }}
                >
                  <p className="text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: '#9B72C0' }}>
                    {t('journal.whatHappened')}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#4B3F62', lineHeight: 1.7 }}>
                    {session.session_summary}
                  </p>
                </div>
              )}

              {/* Saved Insight Cards */}
              {savedCards.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
                    {t('journal.insightCardsReceived')}
                  </p>
                  <div className="flex flex-col gap-3">
                    {savedCards.map(card => (
                      <div
                        key={card.id}
                        className="rounded-xl px-5 py-4"
                        style={{ background: CARD_TYPE_GRADIENTS[card.card_type] ?? CARD_TYPE_GRADIENTS.pattern_archetype }}
                      >
                        <p className="text-[10px] font-medium uppercase tracking-widest mb-2"
                          style={{ color: 'rgba(255,255,255,0.45)' }}>
                          {t(`journal.cardTypes.${card.card_type}`, { defaultValue: card.card_type })}
                        </p>
                        <p className="text-sm font-display italic mb-2"
                          style={{ color: 'white', fontWeight: 400 }}>
                          {card.card_title}
                        </p>
                        <div style={{ width: '24px', height: '1px', background: 'rgba(255,255,255,0.18)', marginBottom: '0.75rem' }} />
                        <p className="text-xs leading-relaxed"
                          style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 }}>
                          {card.card_message}
                        </p>
                        {/* Re-share a previously saved card as a 9:16 image */}
                        {card.card_title && card.card_message && (
                          <button
                            onClick={() => {
                              const stops = (CARD_TYPE_GRADIENTS[card.card_type] ?? CARD_TYPE_GRADIENTS.pattern_archetype).match(/#[0-9A-Fa-f]{6}/g) || ['#3D1F62', '#6B3FA0']
                              void saveInsightCard({
                                cardLabel: t(`journal.cardTypes.${card.card_type}`, { defaultValue: card.card_type }),
                                cardTitle: card.card_title ?? '',
                                cardMessage: card.card_message ?? '',
                                gradientFrom: stops[0],
                                gradientTo: stops[1] || stops[0],
                                emotion: session.emotion_label,
                                intensity: session.emotion_intensity,
                                date,
                              })
                            }}
                            className="mt-3 text-xs font-medium"
                            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '9999px', padding: '0.4rem 0.9rem', cursor: 'pointer' }}
                          >
                            {t('journal.saveAsImage')}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {healingPhrase && (
                <div
                  className="px-4 py-3 rounded-xl text-center"
                  style={{
                    background: 'rgba(155,114,192,0.04)',
                    borderTop: '3px solid #9B72C0',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}
                >
                  <p className="text-xs font-display italic leading-relaxed" style={{ color: '#6B3FA0' }}>
                    {healingPhrase}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Personal Symbol ──────────────────────────────────────────────────────────

function PersonalSymbol({ symbol, onSelect }: { symbol: string | null; onSelect: (id: string) => void }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const current = SYMBOLS.find(s => s.id === symbol)

  return (
    <div className="flex flex-col items-center">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex flex-col items-center gap-2 group"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center transition-all"
          style={{
            background: symbol ? 'white' : '#F4EFF8',
            border: `1.5px solid ${symbol ? '#CEBCDC' : '#E4D5EE'}`,
            boxShadow: symbol ? '0 2px 12px rgba(61,31,98,0.1)' : 'none',
          }}
        >
          {current ? (
            <svg width="32" height="32" viewBox="0 0 100 100" fill="none">
              <path d={current.path} stroke="#3D1F62" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 4v10M4 9h10" stroke="#C4B5D4" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </div>
        <p className="text-[10px]" style={{ color: '#9B8FAA' }}>
          {current ? t(`journal.symbols.${current.id}`) : t('journal.chooseSymbol')}
        </p>
      </button>

      {open && (
        <div
          className="mt-3 p-3 rounded-2xl message-appear"
          style={{ background: 'white', border: '1.5px solid #E4D5EE', boxShadow: '0 4px 20px rgba(61,31,98,0.1)' }}
        >
          <p className="text-[10px] font-medium uppercase tracking-widest mb-3 text-center" style={{ color: '#9B8FAA' }}>
            {t('journal.yourSymbol')}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {SYMBOLS.map(s => (
              <button
                key={s.id}
                onClick={() => { onSelect(s.id); setOpen(false) }}
                className="w-14 h-14 rounded-xl flex items-center justify-center transition-all"
                style={{
                  background: symbol === s.id ? '#F4EFF8' : 'transparent',
                  border: `1.5px solid ${symbol === s.id ? '#CEBCDC' : '#E4D5EE'}`,
                  cursor: 'pointer',
                }}
              >
                <svg width="30" height="30" viewBox="0 0 100 100" fill="none">
                  <path d={s.path} stroke="#3D1F62" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function Journal({ user }: JournalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // i18n.language drives everything t() cannot: date formatting, plural
  // category selection, and the data-table localizers (localizeSeries,
  // localizeEmotionLabel) that translate copy stored outside the locale files.
  const { i18n } = useTranslation()
  const [sessions, setSessions] = useState<SessionData[]>([])
  const [insightCards, setInsightCards] = useState<InsightCardRecord[]>([])
  const [personalSymbol, setPersonalSymbol] = useState<string | null>(null)
  const [subscriptionTier, setSubscriptionTier] = useState<string>('free')
  const [firstName, setFirstName] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [patternSynthesis, setPatternSynthesis] = useState<string | null>(null)
  const [synthLoading, setSynthLoading] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  // Annual is pre-selected: approved 2026-09-05, PRICING_TQO_TIA.md section 12.
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year')
  const [patterns, setPatterns] = useState<PatternRecord[]>([])
  const [seriesProgress, setSeriesProgress] = useState<Record<string, SeriesProgressRecord>>({})
  const [expandedSeries, setExpandedSeries] = useState<Record<string, boolean>>({})
  const [marksLegendOpen, setMarksLegendOpen] = useState(false)
  // Phase 2 — Archetype System (Awakening only)
  const [archetypes, setArchetypes] = useState<ArchetypeWithDef[]>([])
  const [archetypesLoading, setArchetypesLoading] = useState(false)
  // Phase 3 — Cross-Session Pattern Intelligence (Awakening only)
  // Established clusters surfaced as "Threads I'm Noticing" — dismissible
  // for 30 days at a time so the user controls the awareness loop.
  const [clusters, setClusters] = useState<ClusterRecord[]>([])
  // Phase 4 — The Becoming (growth + premium). Monthly longitudinal narrative.
  // Fetched/generated on journal load; null means not ready (< 3 sessions) or loading.
  const [becoming, setBecoming] = useState<BecomingRecord | null>(null)
  const [becomingLoading, setBecomingLoading] = useState(false)
  // Arc Letters — saved letters from healing arc sessions (Awakening only).
  // Burned letters never appear here — they leave no record.
  const [arcLetters, setArcLetters] = useState<ArcLetterRecord[]>([])

  useEffect(() => {
    Promise.all([
      db()
        .from('sessions')
        .select('id, emotion_label, emotion_intensity, phase_reached, is_complete, is_favorite, tags, session_summary, pattern_reflection, choice_text, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
      db()
        .from('insight_cards')
        .select('id, session_id, card_type, card_title, card_message, saved, created_at')
        .eq('user_id', user.id)
        .eq('saved', true)
        .order('created_at', { ascending: true }),
      db()
        .from('profiles')
        .select('personal_symbol, subscription_tier, display_name')
        .eq('id', user.id)
        .maybeSingle(),
      db()
        .from('patterns')
        .select('id, pattern_label, occurrence_count, first_seen_at, last_seen_at, session_ids')
        .eq('user_id', user.id)
        .order('occurrence_count', { ascending: false }),
      db()
        .from('user_series_progress')
        .select('series_id, current_position, completed_at')
        .eq('user_id', user.id),
      db()
        .from('arc_letters')
        .select('id, arc_id, session_number, letter_content, saved_at')
        .eq('user_id', user.id)
        .order('saved_at', { ascending: false }),
    ]).then(([sessionsRes, cardsRes, profileRes, patternsRes, progressRes, lettersRes]) => {
      const loadedSessions = (sessionsRes.data as SessionData[]) ?? []
      setSessions(loadedSessions)
      setInsightCards((cardsRes.data as InsightCardRecord[]) ?? [])
      setPatterns((patternsRes.data as PatternRecord[]) ?? [])
      const progressMap: Record<string, SeriesProgressRecord> = {}
      for (const row of (progressRes.data as SeriesProgressRecord[]) ?? []) {
        progressMap[row.series_id] = row
      }
      setSeriesProgress(progressMap)
      const profile = profileRes.data as { personal_symbol: string | null; subscription_tier: string; display_name: string | null } | null
      setPersonalSymbol(profile?.personal_symbol ?? null)
      setSubscriptionTier(profile?.subscription_tier ?? 'free')
      setFirstName(profile?.display_name ?? '')
      setArcLetters((lettersRes.data as ArcLetterRecord[]) ?? [])
      setLoading(false)

      // For premium users: generate cross-session pattern synthesis if 2+ completed sessions have patterns
      if (profile?.subscription_tier === 'premium') {
        const patterns = loadedSessions
          .filter(s => s.is_complete && s.pattern_reflection)
          .map(s => s.pattern_reflection!)
        if (patterns.length >= 2) {
          setSynthLoading(true)
          callClaude({
            phase: 11,
            emotion: '',
            intensity: 0,
            messages: [{ role: 'user', content: '[Generate synthesis]' }],
            firstName: profile?.display_name ?? '',
            sessionPatterns: patterns,
          }).then(result => {
            if (!('error' in result)) setPatternSynthesis(result.response)
            setSynthLoading(false)
          })
        }

        // Phase 3 — Load established clusters (occurrence_count >= 2,
        // not currently dismissed). Surfaced as "Threads I'm Noticing".
        // Awakening tier only — non-blocking from the archetype load.
        const nowIso = new Date().toISOString()
        db()
          .from('pattern_clusters')
          .select('id, cluster_label, occurrence_count, first_seen_at, dismissed_until')
          .eq('user_id', user.id)
          .or(`dismissed_until.is.null,dismissed_until.lt.${nowIso}`)
          .gte('occurrence_count', 2)
          .order('occurrence_count', { ascending: false })
          .limit(10)
          .then(({ data: clusterData }) => {
            if (clusterData) setClusters(clusterData as ClusterRecord[])
          })

        // Phase 2 — Load archetype assignments (Awakening only).
        // Two-step query: assignments first, then definitions, joined client-side.
        // (Supabase JS client does not support cross-table joins on custom schemas.)
        setArchetypesLoading(true)
        db()
          .from('user_archetype_assignments')
          .select('id, archetype_id, state, intensity, observation_count, user_confirmed, user_rejected, evidence_quote, first_seen_at')
          .eq('user_id', user.id)
          .eq('user_rejected', false)
          .order('intensity', { ascending: false })
          .then(async ({ data: assignments }) => {
            const list = (assignments as ArchetypeAssignment[] | null) ?? []
            if (list.length === 0) {
              setArchetypes([])
              setArchetypesLoading(false)
              return
            }
            const ids = list.map(a => a.archetype_id)
            const { data: defs } = await db()
              .from('archetype_definitions')
              .select('id, display_name, description, shadow_aspect, integration_aspect')
              .in('id', ids)

            // Build a quick lookup map and filter out any orphaned assignments.
            // Overlay the locale's copy so a Polish user reads the pattern in Polish.
            const defMap = new Map((defs as ArchetypeDefinition[] ?? []).map(d => [d.id, localizeArchetype(d.id, d, t)]))
            const combined: ArchetypeWithDef[] = list
              .filter(a => defMap.has(a.archetype_id))
              .map(a => ({ ...a, definition: defMap.get(a.archetype_id)! }))
            setArchetypes(combined)
            setArchetypesLoading(false)
          })
      }

      // Phase 4 — The Becoming (growth + premium).
      // Fetch or generate the monthly longitudinal narrative. Fire-and-store —
      // never blocks the journal render. growth users get sessions-only narrative;
      // premium gets sessions + archetypes + clusters (handled server-side).
      const tier = profile?.subscription_tier ?? 'free'
      if (tier === 'growth' || tier === 'premium') {
        const completedCount = loadedSessions.filter(s => s.is_complete).length
        if (completedCount >= 3) {
          setBecomingLoading(true)
          fetchBecoming().then(result => {
            setBecoming(result)
            setBecomingLoading(false)
          })
        }
      }
    })
  }, [user.id])

  // Phase 2 — Confirmation handler for archetype cards.
  // confirmed=true → mark active + user_confirmed; confirmed=false → permanent suppression.
  async function handleArchetypeConfirm(assignmentId: string, confirmed: boolean) {
    if (confirmed) {
      await db()
        .from('user_archetype_assignments')
        .update({
          state: 'active',
          user_confirmed: true,
          surfaced_at: new Date().toISOString(),
        })
        .eq('id', assignmentId)
      setArchetypes(prev =>
        prev.map(a => a.id === assignmentId
          ? { ...a, state: 'active', user_confirmed: true }
          : a
        )
      )
    } else {
      await db()
        .from('user_archetype_assignments')
        .update({ user_rejected: true })
        .eq('id', assignmentId)
      // Filter out — once rejected, never shown again
      setArchetypes(prev => prev.filter(a => a.id !== assignmentId))
    }
  }

  // Phase 3 — Dismiss a cluster from "Threads I'm Noticing" for 30 days.
  // After 30 days the cluster reappears in surfacing queries (and the
  // SessionPage cluster-context fetch). Permanent suppression is intentional
  // — patterns evolve, and this gives the user agency without permanent loss.
  async function handleClusterDismiss(clusterId: string) {
    const dismissUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    await db()
      .from('pattern_clusters')
      .update({ dismissed_until: dismissUntil })
      .eq('id', clusterId)
    setClusters(prev => prev.filter(c => c.id !== clusterId))
  }

  // Fire upgrade_prompt_shown once per session load when the user encounters a gate
  useEffect(() => {
    if (loading || sessions.length === 0) return
    if (subscriptionTier !== 'premium') trackUpgradePromptShown('premium', 'journal')
    if (subscriptionTier === 'free' && sessions.length > 1) trackUpgradePromptShown('growth', 'journal_history')
  }, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSymbolSelect(symbolId: string) {
    setPersonalSymbol(symbolId)
    await db()
      .from('profiles')
      .upsert({ id: user.id, personal_symbol: symbolId }, { onConflict: 'id' })
  }

  const completedCount = sessions.filter(s => s.is_complete).length
  const totalCount = sessions.length
  const savedCardCount = insightCards.length

  const weatherDots = sessions.slice(0, 12).reverse()

  return (
    <div className="min-h-svh flex flex-col" style={{ background: 'radial-gradient(ellipse at 65% 0%, rgba(155,114,192,0.09) 0%, transparent 50%), radial-gradient(ellipse at 10% 85%, rgba(74,128,192,0.05) 0%, transparent 40%), #FAF7F4' }}>

      {/* Header */}
      <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
        <button
          onClick={() => navigate('/')}
          aria-label={t('journal.back')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#6B5F7A' }}
          className="hover:opacity-70 transition-opacity"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="font-display text-lg flex-1" style={{ color: '#1C1128', fontWeight: 400 }}>
          {t('journal.innerSanctuary')}
        </h1>
        <PersonalSymbol symbol={personalSymbol} onSelect={handleSymbolSelect} />
      </header>

      <main className="flex-1 px-6 pb-16 max-w-xl mx-auto w-full">

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-5 h-5 rounded-full border-2 border-[#3D1F62] border-t-transparent animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-sm" style={{ color: '#9B8FAA' }}>{t('journal.noSessionsYet')}</p>
            <button
              onClick={() => navigate('/session', { state: { fresh: true } })}
              className="mt-4 px-6 py-3 rounded-xl text-sm font-medium"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('journal.beginFirst')}
            </button>
          </div>
        ) : (
          <>
            {/* Stats row — each card uses an accent color (violet/blue/teal) on the top border */}
            <div className="flex gap-3 mb-6">
              <div
                className="flex-1 px-4 py-3 rounded-xl text-center"
                style={{
                  background: 'rgba(155,114,192,0.04)',
                  borderTop: '3px solid #9B72C0',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '1.5px solid #E4D5EE',
                }}
              >
                <p className="text-2xl font-display" style={{ color: '#3D1F62', fontWeight: 400 }}>{completedCount}</p>
                <p className="text-xs mt-0.5" style={{ color: '#9B8FAA' }}>{t('journal.statCompleted')}</p>
              </div>
              <div
                className="flex-1 px-4 py-3 rounded-xl text-center"
                style={{
                  background: 'rgba(74,128,192,0.04)',
                  borderTop: '3px solid #4A80C0',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '1.5px solid #E4D5EE',
                }}
              >
                <p className="text-2xl font-display" style={{ color: '#3D1F62', fontWeight: 400 }}>{totalCount}</p>
                <p className="text-xs mt-0.5" style={{ color: '#9B8FAA' }}>{t('journal.statTotal')}</p>
              </div>
              {savedCardCount > 0 && (
                <div
                  className="flex-1 px-4 py-3 rounded-xl text-center"
                  style={{
                    background: 'rgba(58,155,122,0.04)',
                    borderTop: '3px solid #3A9B7A',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}
                >
                  <p className="text-2xl font-display" style={{ color: '#3D1F62', fontWeight: 400 }}>{savedCardCount}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#9B8FAA' }}>{t('journal.cardsSaved')}</p>
                </div>
              )}
            </div>

            {/* Patterns across time — Premium feature */}
            {subscriptionTier === 'premium' ? (
              completedCount >= 2 ? (
                <div className="mb-6">
                  <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
                    {t('journal.patternsAcrossTime')}
                  </p>
                  {synthLoading ? (
                    <div
                      className="px-5 py-5 rounded-2xl"
                      style={{
                        background: 'linear-gradient(145deg, #F4EFF8 0%, #EDE3F5 100%)',
                        borderTop: '3px solid #9B72C0',
                        borderRight: '1.5px solid #CEBCDC',
                        borderBottom: '1.5px solid #CEBCDC',
                        borderLeft: '1.5px solid #CEBCDC',
                      }}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-4 h-4 rounded-full border-2 border-[#9B72C0] border-t-transparent animate-spin flex-shrink-0" />
                        <p className="text-sm font-display italic" style={{ color: '#9B8FAA' }}>{t('journal.reflectingAcross')}</p>
                      </div>
                    </div>
                  ) : patternSynthesis ? (
                    <div
                      className="px-5 py-6 rounded-2xl message-appear"
                      style={{
                        background: 'linear-gradient(145deg, #F4EFF8 0%, #EDE3F5 100%)',
                        borderTop: '3px solid #9B72C0',
                        borderRight: '1.5px solid #CEBCDC',
                        borderBottom: '1.5px solid #CEBCDC',
                        borderLeft: '1.5px solid #CEBCDC',
                        boxShadow: '0 4px 20px rgba(61,31,98,0.07)',
                      }}
                    >
                      <p className="text-sm leading-relaxed font-display italic" style={{ color: '#3D1F62', lineHeight: 1.75 }}>
                        {patternSynthesis}
                      </p>
                      <div style={{ width: '32px', height: '1px', background: 'rgba(61,31,98,0.12)', margin: '1.25rem 0 0.75rem' }} />
                      <p className="text-[10px] font-medium uppercase tracking-widest" style={{ color: '#C4B5D4' }}>
                        {t(`journal.basedOnSessions.${pluralCategory(completedCount, i18n.language)}`, { count: completedCount })}
                      </p>
                    </div>
                  ) : (
                    <div className="px-5 py-4 rounded-2xl" style={{ background: '#F4EFF8', border: '1px solid #E4D5EE' }}>
                      <p className="text-sm" style={{ color: '#9B8FAA' }}>{t('journal.synthesisAfterNext')}</p>
                    </div>
                  )}

                  {/* Recurring patterns list */}
                  {patterns.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {patterns.map(p => (
                        <div
                          key={p.id}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl"
                          style={{
                            background: 'rgba(155,114,192,0.03)',
                            borderLeft: '3px solid #9B72C0',
                            borderTop: '1px solid #E4D5EE',
                            borderRight: '1px solid #E4D5EE',
                            borderBottom: '1px solid #E4D5EE',
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm capitalize" style={{ color: '#3D1F62' }}>{p.pattern_label}</p>
                            <p className="text-[10px] mt-0.5" style={{ color: '#C4B5D4' }}>
                              {t('journal.firstSeen', {
                                date: new Date(p.first_seen_at).toLocaleDateString(i18n.language, { month: 'short', year: 'numeric' }),
                              })}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {Array.from({ length: Math.min(p.occurrence_count, 5) }).map((_, i) => (
                              <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: '#9B72C0' }} />
                            ))}
                            {p.occurrence_count > 5 && (
                              <span className="text-[10px] ml-1" style={{ color: '#9B72C0' }}>+{p.occurrence_count - 5}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-6 px-5 py-4 rounded-2xl" style={{ background: '#F4EFF8', border: '1px solid #E4D5EE' }}>
                  <p className="text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: '#9B72C0' }}>{t('journal.patternsAcrossTime')}</p>
                  <p className="text-sm" style={{ color: '#9B8FAA' }}>
                    {t('journal.completeTwoOrMore')}
                  </p>
                </div>
              )
            ) : (
              <div
                className="mb-6 px-5 py-5 rounded-2xl relative overflow-hidden"
                style={{ background: 'linear-gradient(145deg, #F4EFF8 0%, #EDE3F5 100%)', border: '1.5px solid #CEBCDC' }}
              >
                <p className="text-xs font-medium uppercase tracking-widest mb-2" style={{ color: '#9B72C0' }}>
                  {t('journal.patternsAcrossTime')}
                </p>
                <p className="text-sm leading-relaxed font-display italic mb-4" style={{ color: '#6B5F7A', filter: 'blur(3.5px)', userSelect: 'none' }}>
                  {firstName ? t('journal.patternTeaser', { name: firstName }) : t('journal.patternTeaserNoName')}
                </p>
                <div className="flex mb-3 p-0.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.6)' }}>
                  <button
                    onClick={() => setBillingInterval('month')}
                    className="flex-1 py-1.5 rounded-xl text-xs font-medium transition-all"
                    style={{ background: billingInterval === 'month' ? '#3D1F62' : 'transparent', color: billingInterval === 'month' ? 'white' : '#9B8FAA', border: 'none', cursor: 'pointer' }}
                  >
                    {t('journal.monthly')}
                  </button>
                  <button
                    onClick={() => setBillingInterval('year')}
                    className="flex-1 py-1.5 rounded-xl text-xs font-medium transition-all"
                    style={{ background: billingInterval === 'year' ? '#3D1F62' : 'transparent', color: billingInterval === 'year' ? 'white' : '#9B8FAA', border: 'none', cursor: 'pointer' }}
                  >
                    {t('journal.annualHalfPrice')}
                  </button>
                </div>
                <button
                  onClick={() => {
                    setCheckoutLoading(true)
                    setCheckoutError(null)
                    redirectToCheckout('premium', billingInterval).then(({ error }) => { if (error) { setCheckoutError(error); setCheckoutLoading(false) } })
                  }}
                  disabled={checkoutLoading}
                  className="w-full py-3 rounded-xl text-sm font-medium"
                  style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer', opacity: checkoutLoading ? 0.7 : 1 }}
                >
                  {checkoutLoading
                    ? t('common.redirecting')
                    : billingInterval === 'year'
                      ? t('journal.beginAwakeningArcAnnual')
                      : t('journal.beginAwakeningArcMonthly')}
                </button>
              </div>
            )}

            {/* ── The Becoming — growth + premium (Phase 4) ───────────────── */}
            {/* Monthly longitudinal narrative generated server-side from the  */}
            {/* full session history. Premium enriches with archetypes and      */}
            {/* clusters. Free tier sees a blurred teaser + Practice CTA.       */}
            {(subscriptionTier === 'growth' || subscriptionTier === 'premium') ? (
              <div className="mb-6">
                {becomingLoading ? (
                  // Loading state — shown while the Edge Function generates the narrative.
                  <div
                    className="px-5 py-6 rounded-2xl"
                    style={{
                      background: 'linear-gradient(145deg, #1E0A36 0%, #2D1854 100%)',
                      borderTop: '3px solid #C4A830',
                      borderRight: '1.5px solid rgba(196,168,48,0.25)',
                      borderBottom: '1.5px solid rgba(196,168,48,0.25)',
                      borderLeft: '1.5px solid rgba(196,168,48,0.25)',
                    }}
                  >
                    <p className="text-[10px] font-medium uppercase tracking-widest mb-4"
                      style={{ color: 'rgba(196,168,48,0.6)' }}>
                      {t('journal.theBecoming')}
                    </p>
                    <div className="flex items-center gap-2.5">
                      <div className="w-4 h-4 rounded-full border-2 flex-shrink-0"
                        style={{ borderColor: '#C4A830', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
                      <p className="text-sm font-display italic" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        {t('journal.witnessingJourney')}
                      </p>
                    </div>
                  </div>
                ) : becoming ? (
                  // Narrative ready — the centrepiece of Phase 4.
                  <div
                    className="px-6 py-7 rounded-2xl message-appear"
                    style={{
                      background: 'linear-gradient(145deg, #1E0A36 0%, #2D1854 100%)',
                      borderTop: '3px solid #C4A830',
                      borderRight: '1.5px solid rgba(196,168,48,0.25)',
                      borderBottom: '1.5px solid rgba(196,168,48,0.25)',
                      borderLeft: '1.5px solid rgba(196,168,48,0.25)',
                      boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
                    }}
                  >
                    <p className="text-[10px] font-medium uppercase tracking-widest mb-1"
                      style={{ color: 'rgba(196,168,48,0.7)' }}>
                      {t('journal.theBecoming')}
                    </p>
                    <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.25)' }}>
                      {new Date(becoming.generated_at).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}
                    </p>
                    {/* Decorative rule */}
                    <div style={{ width: '28px', height: '1px', background: 'rgba(196,168,48,0.3)', marginBottom: '1.5rem' }} />
                    <p className="font-display italic leading-relaxed"
                      style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', lineHeight: 1.8 }}>
                      {becoming.narrative_text}
                    </p>
                    <div style={{ width: '28px', height: '1px', background: 'rgba(196,168,48,0.3)', margin: '1.5rem 0 0.75rem' }} />
                    <p className="text-[10px] font-medium uppercase tracking-widest"
                      style={{ color: 'rgba(255,255,255,0.2)' }}>
                      {t(`journal.becomingBasedOn.${pluralCategory(becoming.session_count, i18n.language)}`, { count: becoming.session_count })}
                    </p>
                  </div>
                ) : completedCount >= 3 ? (
                  // 3+ sessions but generation failed or returned null — show placeholder.
                  <div
                    className="px-5 py-5 rounded-2xl"
                    style={{
                      background: 'rgba(28,10,54,0.04)',
                      borderTop: '3px solid #C4A830',
                      borderRight: '1.5px solid #E4D5EE',
                      borderBottom: '1.5px solid #E4D5EE',
                      borderLeft: '1.5px solid #E4D5EE',
                    }}
                  >
                    <p className="text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: '#C4A830' }}>
                      {t('journal.theBecoming')}
                    </p>
                    <p className="text-sm" style={{ color: '#9B8FAA' }}>
                      {t('journal.narrativePreparing')}
                    </p>
                  </div>
                ) : (
                  // Below the 3-session minimum — show how many more are needed.
                  <div
                    className="px-5 py-5 rounded-2xl"
                    style={{
                      background: 'rgba(28,10,54,0.04)',
                      borderTop: '3px solid #C4A830',
                      borderRight: '1.5px solid #E4D5EE',
                      borderBottom: '1.5px solid #E4D5EE',
                      borderLeft: '1.5px solid #E4D5EE',
                    }}
                  >
                    <p className="text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: '#C4A830' }}>
                      {t('journal.theBecoming')}
                    </p>
                    <p className="text-sm" style={{ color: '#9B8FAA' }}>
                      {t(`journal.completeMoreForNarrative.${pluralCategory(3 - completedCount, i18n.language)}`, { count: 3 - completedCount })}
                    </p>
                  </div>
                )}
              </div>
            ) : subscriptionTier === 'free' && completedCount >= 1 ? (
              // Free tier teaser — blurred narrative preview + upgrade CTA.
              <div
                className="mb-6 px-5 py-6 rounded-2xl relative overflow-hidden"
                style={{
                  background: 'linear-gradient(145deg, #1E0A36 0%, #2D1854 100%)',
                  borderTop: '3px solid #C4A830',
                  borderRight: '1.5px solid rgba(196,168,48,0.25)',
                  borderBottom: '1.5px solid rgba(196,168,48,0.25)',
                  borderLeft: '1.5px solid rgba(196,168,48,0.25)',
                }}
              >
                <p className="text-[10px] font-medium uppercase tracking-widest mb-4"
                  style={{ color: 'rgba(196,168,48,0.7)' }}>
                  {t('journal.theBecoming')}
                </p>
                {/* Blurred placeholder text — gives the feel without the content */}
                <p className="font-display italic leading-relaxed mb-5"
                  style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', lineHeight: 1.8, filter: 'blur(4px)', userSelect: 'none' }}>
                  {firstName ? t('journal.becomingTeaser', { name: firstName }) : t('journal.becomingTeaserNoName')}
                </p>
                <div className="flex mb-3 p-0.5 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <button
                    onClick={() => setBillingInterval('month')}
                    className="flex-1 py-1.5 rounded-xl text-xs font-medium transition-all"
                    style={{ background: billingInterval === 'month' ? 'rgba(255,255,255,0.15)' : 'transparent', color: billingInterval === 'month' ? 'white' : 'rgba(255,255,255,0.4)', border: 'none', cursor: 'pointer' }}
                  >
                    {t('journal.monthly')}
                  </button>
                  <button
                    onClick={() => setBillingInterval('year')}
                    className="flex-1 py-1.5 rounded-xl text-xs font-medium transition-all"
                    style={{ background: billingInterval === 'year' ? 'rgba(255,255,255,0.15)' : 'transparent', color: billingInterval === 'year' ? 'white' : 'rgba(255,255,255,0.4)', border: 'none', cursor: 'pointer' }}
                  >
                    {t('journal.annualSave')}
                  </button>
                </div>
                <button
                  onClick={() => {
                    setCheckoutLoading(true)
                    setCheckoutError(null)
                    redirectToCheckout('growth', billingInterval).then(({ error }) => { if (error) { setCheckoutError(error); setCheckoutLoading(false) } })
                  }}
                  disabled={checkoutLoading}
                  className="w-full py-3 rounded-xl text-sm font-medium"
                  style={{ background: 'rgba(196,168,48,0.9)', color: '#1E0A36', border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer', opacity: checkoutLoading ? 0.7 : 1, fontWeight: 600 }}
                >
                  {checkoutLoading
                    ? t('common.redirecting')
                    : billingInterval === 'year'
                      ? t('journal.practiceAnnual')
                      : t('journal.practiceMonthly')}
                </button>
              </div>
            ) : null}

            {/* ── Awakening intelligence teaser — Practice tier only ──────────── */}
            {/* Shown to growth users with 3+ sessions. Blurred placeholder rows  */}
            {/* make the threads feel real and present without revealing content. */}
            {/* Tim: "convert the gap into upgrade pressure."                      */}
            {/* Tracey: "a guide that remembers" is the Awakening differentiator.  */}
            {subscriptionTier === 'growth' && completedCount >= 3 && (
              <div className="mb-10">
                <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#4A80C0' }}>
                  {t('journal.threadsNoticing')}
                </p>
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{
                    background: 'white',
                    borderTop: '3px solid #4A80C0',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}
                >
                  {/* Blurred thread rows — gives the shape without the content */}
                  <div className="px-5 pt-5 pb-3 space-y-2.5"
                    style={{ filter: 'blur(3.5px)', userSelect: 'none', pointerEvents: 'none' }}>
                    {[3, 2, 2].map((dots, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                        style={{ background: 'rgba(74,128,192,0.04)', border: '1.5px solid #E4D5EE' }}>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {Array.from({ length: dots }).map((_, j) => (
                            <div key={j} className="w-1.5 h-1.5 rounded-full"
                              style={{ background: '#4A80C0', opacity: 0.3 + j * 0.2 }} />
                          ))}
                        </div>
                        {/* Fake label bar — width varies to feel organic */}
                        <div className="h-3 rounded-full"
                          style={{ background: '#E4D5EE', width: `${60 + i * 15}%` }} />
                      </div>
                    ))}
                  </div>

                  {/* Upgrade pitch */}
                  <div className="px-5 pb-5">
                    <p className="text-xs leading-relaxed mb-4" style={{ color: '#6B5F7A', lineHeight: 1.65 }}>
                      {t('journal.practiceAwakeningPitch')}
                    </p>
                    <div className="flex mb-3 p-0.5 rounded-xl" style={{ background: '#F4EFF8', border: '1px solid #E4D5EE' }}>
                      <button
                        onClick={() => setBillingInterval('month')}
                        className="flex-1 py-1.5 rounded-xl text-xs font-medium transition-all"
                        style={{ background: billingInterval === 'month' ? '#3D1F62' : 'transparent', color: billingInterval === 'month' ? 'white' : '#9B8FAA', border: 'none', cursor: 'pointer' }}
                      >
                        {t('journal.monthly')}
                      </button>
                      <button
                        onClick={() => setBillingInterval('year')}
                        className="flex-1 py-1.5 rounded-xl text-xs font-medium transition-all"
                        style={{ background: billingInterval === 'year' ? '#3D1F62' : 'transparent', color: billingInterval === 'year' ? 'white' : '#9B8FAA', border: 'none', cursor: 'pointer' }}
                      >
                        {t('journal.annualHalfPrice')}
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setCheckoutLoading(true)
                        setCheckoutError(null)
                        redirectToCheckout('premium', billingInterval).then(({ error }) => { if (error) { setCheckoutError(error); setCheckoutLoading(false) } })
                      }}
                      disabled={checkoutLoading}
                      className="w-full py-3 rounded-xl text-sm font-medium"
                      style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer', opacity: checkoutLoading ? 0.7 : 1 }}
                    >
                      {checkoutLoading
                        ? t('common.redirecting')
                        : billingInterval === 'year'
                          ? t('journal.beginAwakeningAnnual')
                          : t('journal.beginAwakeningMonthly')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Patterns I'm Holding — Awakening tier only ───────────────── */}
            {/* Surfaces classifier-detected archetypes. Each card shows the   */}
            {/* archetype name, description, evidence quote, and (for emerging */}
            {/* assignments) confirmation buttons. Confirmed cards display the */}
            {/* integration aspect. Rejected cards are filtered permanently.   */}
            {subscriptionTier === 'premium' && (archetypesLoading || archetypes.length > 0) && (
              <div className="mb-10">
                <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
                  {t('journal.patternsHolding')}
                </p>

                {archetypesLoading && (
                  <p className="text-xs" style={{ color: '#9B8FAA' }}>{t('journal.loading')}</p>
                )}

                {!archetypesLoading && (
                  <div className="flex flex-col gap-3">
                    {archetypes.map(a => (
                      <div
                        key={a.id}
                        className="rounded-2xl px-5 py-5"
                        style={{
                          // Confirmed archetypes get a soft violet wash;
                          // emerging/unconfirmed ones stay on white to feel less claimed.
                          background: a.user_confirmed
                            ? 'linear-gradient(145deg, #F9F5FF 0%, #F1E8F8 100%)'
                            : 'white',
                          borderTop: '3px solid #7B40A8',
                          borderRight: '1.5px solid #E4D5EE',
                          borderBottom: '1.5px solid #E4D5EE',
                          borderLeft: '1.5px solid #E4D5EE',
                        }}
                      >
                        {/* Archetype name + state badge */}
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#7B40A8' }}>
                            {a.definition.display_name}
                          </p>
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full"
                            style={{
                              background: a.state === 'active' ? 'rgba(123,64,168,0.12)' : 'rgba(155,114,192,0.06)',
                              color: a.state === 'active' ? '#7B40A8' : '#9B8FAA',
                              border: '1px solid #E4D5EE',
                            }}
                          >
                            {t(`journal.archetypeState.${a.state}`, { defaultValue: t('journal.archetypeState.released') })}
                          </span>
                        </div>

                        {/* Description — what this pattern looks like */}
                        <p className="text-sm leading-relaxed mb-3" style={{ color: '#4B3F62', lineHeight: 1.65 }}>
                          {a.definition.description}
                        </p>

                        {/* Evidence quote from the classifier — keeps it grounded in the user's words */}
                        {a.evidence_quote && (
                          <p className="text-xs italic mb-3" style={{ color: '#9B8FAA', lineHeight: 1.6 }}>
                            "{a.evidence_quote}"
                          </p>
                        )}

                        {/* Integration aspect — only revealed once the user confirms the archetype */}
                        {a.user_confirmed && (
                          <div
                            className="px-4 py-3 rounded-xl mt-2"
                            style={{
                              background: 'rgba(123,64,168,0.04)',
                              borderLeft: '3px solid #7B40A8',
                              borderTop: '1px solid #E4D5EE',
                              borderRight: '1px solid #E4D5EE',
                              borderBottom: '1px solid #E4D5EE',
                            }}
                          >
                            <p className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: '#7B40A8' }}>
                              {t('journal.integration')}
                            </p>
                            <p className="text-sm leading-relaxed" style={{ color: '#4B3F62', lineHeight: 1.65 }}>
                              {a.definition.integration_aspect}
                            </p>
                          </div>
                        )}

                        {/* Confirmation UI — only shown for unconfirmed, still-emerging assignments */}
                        {!a.user_confirmed && a.state === 'emerging' && (
                          <div className="mt-4 pt-3" style={{ borderTop: '1px solid #F4EFF8' }}>
                            <p className="text-xs mb-3" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
                              {t('journal.noticedPattern')}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleArchetypeConfirm(a.id, true)}
                                className="flex-1 py-2.5 rounded-xl text-sm font-medium"
                                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                              >
                                {t('journal.yesFeelsTrue')}
                              </button>
                              <button
                                onClick={() => handleArchetypeConfirm(a.id, false)}
                                className="flex-1 py-2.5 rounded-xl text-sm"
                                style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#9B8FAA', cursor: 'pointer' }}
                              >
                                {t('journal.notForMe')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Threads I'm Noticing — Awakening tier only (Phase 3) ─────── */}
            {/* Surfaces established pattern clusters from cross-session signal */}
            {/* extraction. Each thread shows the cluster label, occurrence     */}
            {/* density (small dots), and a dismiss action that suppresses it   */}
            {/* from context injection for 30 days.                             */}
            {subscriptionTier === 'premium' && clusters.length > 0 && (
              <div className="mb-10">
                <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#4A80C0' }}>
                  {t('journal.threadsNoticing')}
                </p>
                <div className="flex flex-col gap-2.5">
                  {clusters.map(c => (
                    <div
                      key={c.id}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{ background: 'white', border: '1.5px solid #E4D5EE' }}
                    >
                      {/* Occurrence indicator — up to 5 dots, opacity ramps from light to dark */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {Array.from({ length: Math.min(c.occurrence_count, 5) }).map((_, i) => (
                          <div key={i} className="w-1.5 h-1.5 rounded-full"
                            style={{ background: '#4A80C0', opacity: 0.3 + i * 0.15 }} />
                        ))}
                      </div>

                      <p className="flex-1 text-sm" style={{ color: '#1C1128' }}>
                        {c.cluster_label}
                      </p>

                      <button
                        onClick={() => handleClusterDismiss(c.id)}
                        className="text-xs flex-shrink-0"
                        style={{ background: 'none', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
                        title={t('journal.dismiss30')}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs mt-3" style={{ color: '#C4B5D4' }}>
                  {t('journal.themesAppear')}
                </p>
              </div>
            )}

            {/* ── Your Letters — Awakening tier, only when letters exist ──── */}
            {/* Saved letters from healing arc sessions (session 3). Burned     */}
            {/* letters leave no record — only saves appear here.               */}
            {subscriptionTier === 'premium' && arcLetters.length > 0 && (
              <div className="mb-10">
                <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
                  {t('journal.yourLetters')}
                </p>
                <div className="flex flex-col gap-3">
                  {arcLetters.map(letter => (
                    <LetterCard key={letter.id} letter={letter} />
                  ))}
                </div>
                <p className="text-xs mt-3 leading-relaxed" style={{ color: '#C4B5D4' }}>
                  {t('journal.lettersKept')}
                </p>
              </div>
            )}

            {/* ── Words I'm Holding — saved affirmation insight_cards ── */}
            {/* insight_cards with card_type='affirmation' and session_id  */}
            {/* null are saved from the Affirmations Preparation (Practice+  */}
            {/* only). They never attach to a session so they don't appear  */}
            {/* in SessionCard — this section is their dedicated display.   */}
            {subscriptionTier !== 'free' && (() => {
              const saved = insightCards.filter(c => c.card_type === 'affirmation')
              if (saved.length === 0) return null
              return (
                <div className="mb-10">
                  <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
                    {t('journal.wordsHolding')}
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {saved.map(card => (
                      <div
                        key={card.id}
                        className="rounded-xl px-4 py-4"
                        style={{
                          background: 'rgba(155,114,192,0.06)',
                          border: '1px solid rgba(155,114,192,0.18)',
                        }}
                      >
                        <p className="text-[10px] font-medium uppercase tracking-widest mb-2" style={{ color: '#9B72C0' }}>
                          {card.card_title}
                        </p>
                        <p
                          className="font-display text-base leading-snug"
                          style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}
                        >
                          {card.card_message}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs mt-3" style={{ color: '#C4B5D4', fontStyle: 'italic' }}>
                    {t('journal.curatedBy')}
                  </p>
                </div>
              )
            })()}

            {/* Emotional weather strip — wrapped in a violet-accent card */}
            {weatherDots.length > 1 && (
              <div
                className="mb-6 px-5 py-5 rounded-2xl"
                style={{
                  background: 'rgba(155,114,192,0.04)',
                  borderTop: '3px solid #9B72C0',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '1.5px solid #E4D5EE',
                }}
              >
                <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
                  {t('journal.emotionalWeather')}
                </p>
                <div className="flex items-end gap-1.5">
                  {weatherDots.map((s, i) => (
                    <div key={s.id} title={localizeEmotionLabel(s.emotion_label, i18n.language)} className="flex flex-col items-center gap-1">
                      <div
                        className="rounded-full"
                        style={{
                          width: 10,
                          height: 10 + (s.emotion_intensity ?? 5) * 2.5,
                          background: emotionColor(s.emotion_label),
                          opacity: s.is_complete ? 1 : 0.4,
                        }}
                      />
                      {i === weatherDots.length - 1 && (
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#9B72C0' }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Emotion frequency — after 3+ completed sessions */}
            {completedCount >= 3 && (() => {
              const counts: Record<string, number> = {}
              sessions.filter(s => s.is_complete).forEach(s => {
                counts[s.emotion_label] = (counts[s.emotion_label] ?? 0) + 1
              })
              const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6)
              const max = sorted[0]?.[1] ?? 1
              return (
                <div
                  className="mb-6 px-5 py-5 rounded-2xl"
                  style={{
                    background: 'rgba(74,128,192,0.04)',
                    borderTop: '3px solid #4A80C0',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}
                >
                  <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#4A80C0' }}>
                    {t('journal.whatYouReturnWith')}
                  </p>
                  <div className="space-y-2">
                    {sorted.map(([emotion, count]) => (
                      // `emotion` stays the raw stored word — it is the React key
                      // and the emotionColor lookup. Only the display text is localized.
                      <div key={emotion} className="flex items-center gap-3">
                        <p className="text-xs capitalize w-24 flex-shrink-0 text-right" style={{ color: '#6B5F7A' }}>
                          {localizeEmotionLabel(emotion, i18n.language)}
                        </p>
                        <div className="flex-1 h-1.5 rounded-full" style={{ background: '#F4EFF8' }}>
                          <div
                            className="h-1.5 rounded-full"
                            style={{
                              width: `${(count / max) * 100}%`,
                              background: emotionColor(emotion),
                              transition: 'width 0.4s ease',
                            }}
                          />
                        </div>
                        <p className="text-xs w-4 flex-shrink-0" style={{ color: '#C4B5D4' }}>{count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {/* Guided series — wrapped in an amber-accent card */}
            <div
              className="mb-6 px-5 py-5 rounded-2xl"
              style={{
                background: 'rgba(184,125,10,0.04)',
                borderTop: '3px solid #B87D0A',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '1.5px solid #E4D5EE',
              }}
            >
              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#B87D0A' }}>
                {t('journal.guidedSeries')}
              </p>
              <div className="space-y-3">
                {SERIES_DATA.map(rawSeries => {
                  /*
                   * Localize once, here, at the top of the render. Everything
                   * downstream — the card copy, and crucially the seriesLaunch
                   * payload handed to SessionPage below — then carries the
                   * translated title, focus_title and intention without each
                   * call site having to remember to translate.
                   */
                  const series = localizeSeries(rawSeries, i18n.language)
                  const unlocked = subscriptionTier === 'premium'
                  const progress = seriesProgress[series.id]
                  const isCompleted = !!progress?.completed_at
                  const nextPosition = progress ? progress.current_position : 1
                  const nextSession = series.sessions.find(s => s.position === nextPosition) ?? series.sessions[0]
                  const sessionsCompleted = progress ? Math.min(nextPosition - 1, series.session_count) : 0

                  return (
                    <div
                      key={series.id}
                      className="px-5 py-4 rounded-2xl"
                      style={{
                        background: unlocked ? 'white' : '#FAFAFA',
                        border: `1.5px solid ${unlocked ? '#CEBCDC' : '#E4D5EE'}`,
                        opacity: unlocked ? 1 : 0.75,
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium" style={{ color: unlocked ? '#1C1128' : '#9B8FAA' }}>
                              {series.title}
                            </p>
                            <span
                              className="text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0"
                              style={{ background: '#F4EFF8', color: '#9B72C0', border: '1px solid #E4D5EE' }}
                            >
                              {series.tag}
                            </span>
                          </div>
                          <p className="text-xs leading-relaxed mb-3" style={{ color: '#9B8FAA', lineHeight: 1.6 }}>
                            {series.description}
                          </p>

                          {/* Progress dots — shown once started */}
                          {unlocked && sessionsCompleted > 0 && (
                            <div className="flex items-center gap-1 mb-3">
                              {Array.from({ length: series.session_count }).map((_, i) => (
                                <div
                                  key={i}
                                  className="rounded-full transition-all"
                                  style={{
                                    width: i < sessionsCompleted ? 7 : 5,
                                    height: i < sessionsCompleted ? 7 : 5,
                                    background: i < sessionsCompleted ? '#9B72C0' : '#E4D5EE',
                                    flexShrink: 0,
                                  }}
                                />
                              ))}
                              <p className="text-[10px] ml-2" style={{ color: '#9B8FAA' }}>
                                {isCompleted
                                  ? t('journal.seriesComplete')
                                  : t('session.sessionOf', { current: nextPosition, total: series.session_count })}
                              </p>
                            </div>
                          )}

                          {/* Session roadmap — expandable path through the arc */}
                          {unlocked && (
                            <div className="mb-3">
                              <button
                                onClick={() => setExpandedSeries(prev => ({ ...prev, [series.id]: !prev[series.id] }))}
                                className="flex items-center gap-1 text-[10px] font-medium"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9B8FAA', padding: 0 }}
                              >
                                <span style={{ display: 'inline-block', transform: expandedSeries[series.id] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>▶</span>
                                {expandedSeries[series.id] ? t('journal.hidePath') : t('journal.seeSessionPath')}
                              </button>
                              {expandedSeries[series.id] && (
                                <div className="mt-2 flex flex-col gap-1.5">
                                  {series.sessions.map(s => {
                                    const done = s.position < nextPosition
                                    const current = s.position === nextPosition && !isCompleted
                                    return (
                                      <div key={s.position} className="flex items-start gap-2.5">
                                        <div
                                          className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5"
                                          style={{
                                            background: done ? '#9B72C0' : current ? 'rgba(155,114,192,0.15)' : '#F0EBF4',
                                            border: current ? '1.5px solid #9B72C0' : 'none',
                                          }}
                                        >
                                          {done && (
                                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                              <path d="M1.5 4l2 2 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          )}
                                          {current && <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#9B72C0' }} />}
                                        </div>
                                        <div>
                                          <p className="text-[11px] font-medium leading-tight" style={{ color: done ? '#9B72C0' : current ? '#1C1128' : '#9B8FAA' }}>
                                            {s.focus_title}
                                          </p>
                                          {current && (
                                            <p className="text-[10px] mt-0.5" style={{ color: '#B87D0A' }}>{t('journal.currentSession')}</p>
                                          )}
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <p className="text-[10px]" style={{ color: '#C4B5D4' }}>
                              {t(`common.sessions.${pluralCategory(series.session_count, i18n.language)}`, { count: series.session_count })}
                            </p>
                            {unlocked ? (
                              isCompleted ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9B72C0' }}>
                                    {t('journal.arcComplete')}
                                  </span>
                                  <button
                                    onClick={() => navigate('/session', {
                                      state: {
                                        seriesLaunch: {
                                          seriesId: series.id,
                                          seriesTitle: series.title,
                                          sessionPosition: series.sessions[0].position,
                                          focusTitle: series.sessions[0].focus_title,
                                          intention: series.sessions[0].intention,
                                          context: series.sessions[0].context,
                                        },
                                      },
                                    })}
                                    className="px-3 py-1 rounded-full text-[10px] font-medium"
                                    style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
                                  >
                                    {t('journal.arcRevisit')}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => navigate('/session', {
                                    state: {
                                      seriesLaunch: {
                                        seriesId: series.id,
                                        seriesTitle: series.title,
                                        sessionPosition: nextSession.position,
                                        focusTitle: nextSession.focus_title,
                                        intention: nextSession.intention,
                                        context: nextSession.context,
                                      },
                                    },
                                  })}
                                  className="px-4 py-1.5 rounded-full text-xs font-medium"
                                  style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                                >
                                  {sessionsCompleted > 0
                                    ? t('journal.arcContinue', { title: nextSession.focus_title })
                                    : t('journal.arcBegin')}
                                </button>
                              )
                            ) : (
                              <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#C4A830' }}>
                                {t('journal.awakening')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {subscriptionTier !== 'premium' && (
                  <button
                    onClick={() => {
                      setCheckoutLoading(true)
                      setCheckoutError(null)
                      redirectToCheckout('premium', billingInterval).then(({ error }) => { if (error) { setCheckoutError(error); setCheckoutLoading(false) } })
                    }}
                    disabled={checkoutLoading}
                    className="w-full py-3 rounded-xl text-sm font-medium"
                    style={{ background: 'linear-gradient(135deg, #7C4500, #C4A830)', color: 'white', border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer', opacity: checkoutLoading ? 0.7 : 1 }}
                  >
                    {checkoutLoading
                      ? t('common.redirecting')
                      : billingInterval === 'year'
                        ? t('journal.unlockSeriesAnnual')
                        : t('journal.unlockSeriesMonthly')}
                  </button>
                )}
              </div>
            </div>

            {/* Session list — full history requires Practice; free shows most recent only. Wrapped in a rose-accent card. */}
            <div
              className="mb-6 px-5 py-5 rounded-2xl"
              style={{
                background: 'rgba(196,82,114,0.04)',
                borderTop: '3px solid #C45272',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '1.5px solid #E4D5EE',
              }}
            >
              {/* Sessions header row with marks legend toggle */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#C45272' }}>
                  {t('journal.sessionsLabel')}
                </p>
                <button
                  onClick={() => setMarksLegendOpen(o => !o)}
                  className="text-xs flex items-center gap-1"
                  style={{ color: '#9B8FAA', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {t('journal.whatAreMarks')}
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ transform: marksLegendOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              {/* Collapsible marks legend */}
              {marksLegendOpen && (
                <div className="mb-4 rounded-xl overflow-hidden" style={{ border: '1px solid #E4D5EE' }}>
                  <p className="text-xs px-4 pt-3 pb-2 leading-relaxed" style={{ color: '#6B5F7A', borderBottom: '1px solid #F0ECF5' }}>
                    {t('journal.marksExplain')}
                  </p>
                  <div className="grid grid-cols-2">
                    {MILESTONES.map((m, i) => (
                      <div
                        key={m.id}
                        className="flex items-center gap-2.5 px-4 py-3"
                        style={{
                          background: 'white',
                          borderRight: i % 2 === 0 ? '1px solid #F0ECF5' : 'none',
                          borderTop: i >= 2 ? '1px solid #F0ECF5' : 'none',
                        }}
                      >
                        <span className="flex-shrink-0" style={{ color: m.color }}>{m.icon}</span>
                        <div>
                          <p className="text-xs font-medium leading-none mb-0.5" style={{ color: m.color }}>{t(`journal.milestones.${m.id}.label`)}</p>
                          <p className="text-[11px] leading-snug" style={{ color: '#9B8FAA' }}>{t(`journal.milestones.${m.id}.description`)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2.5">
                {(subscriptionTier === 'free' ? sessions.slice(0, 1) : sessions).map(s => (
                  <SessionCard key={s.id} session={s} insightCards={insightCards} />
                ))}
              </div>

              {/* Practice upsell — shown only when free tier has hidden history */}
              {subscriptionTier === 'free' && sessions.length > 1 && (
                <div
                  className="mt-4 px-5 py-4 rounded-2xl"
                  style={{ background: 'white', border: '1.5px solid #E4D5EE' }}
                >
                  <p className="text-sm font-medium mb-1" style={{ color: '#3D1F62' }}>
                    {t(`journal.moreInHistory.${pluralCategory(sessions.length - 1, i18n.language)}`, { count: sessions.length - 1 })}
                  </p>
                  <p className="text-xs mb-3" style={{ color: '#9B8FAA' }}>
                    {t('journal.practiceUnlocksHistory')}
                  </p>
                  <div className="flex mb-3 p-0.5 rounded-xl" style={{ background: '#F4EFF8', border: '1px solid #E4D5EE' }}>
                    <button
                      onClick={() => setBillingInterval('month')}
                      className="flex-1 py-1.5 rounded-xl text-xs font-medium transition-all"
                      style={{ background: billingInterval === 'month' ? '#3D1F62' : 'transparent', color: billingInterval === 'month' ? 'white' : '#9B8FAA', border: 'none', cursor: 'pointer' }}
                    >
                      {t('journal.monthly')}
                    </button>
                    <button
                      onClick={() => setBillingInterval('year')}
                      className="flex-1 py-1.5 rounded-xl text-xs font-medium transition-all"
                      style={{ background: billingInterval === 'year' ? '#3D1F62' : 'transparent', color: billingInterval === 'year' ? 'white' : '#9B8FAA', border: 'none', cursor: 'pointer' }}
                    >
                      {t('journal.annualSave')}
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      setCheckoutLoading(true)
                      setCheckoutError(null)
                      redirectToCheckout('growth', billingInterval).then(({ error }) => { if (error) { setCheckoutError(error); setCheckoutLoading(false) } })
                    }}
                    disabled={checkoutLoading}
                    className="w-full py-2.5 rounded-xl text-sm font-medium"
                    style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer', opacity: checkoutLoading ? 0.7 : 1 }}
                  >
                    {checkoutLoading
                      ? t('common.redirecting')
                      : billingInterval === 'year'
                        ? t('journal.practiceAnnual')
                        : t('journal.practiceMonthly')}
                  </button>
                  {checkoutError && (
                    <p className="text-xs mt-2 text-center" style={{ color: '#f87171' }}>{checkoutError}</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  )
}
